import React from 'react';
import s from './s.module.less';
import dayjs from 'utils/dayjs';
import { Button, Space, Dropdown, Input, message, Popover, Select } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import {
    ECredentialingDepositBillingStatus,
    ECredentialingDepositBillingStatusLabel,
    TCredentialingDepositItem,
} from 'types/financials';
import { ColumnType } from 'antd/es/table';
import CommonTable from 'components/CommonTable';
import useCredentialingDepositList from 'hooks/useCredentialingDepositList';
import useCredentialingDepositSummary from 'hooks/useCredentialingDepositSummary';
import { isLocalhost } from 'utils/common';
import { getUserToken } from 'utils/localstore';
import axios from 'axios';
import { getInvoiceViewUrl } from 'api/financials';
import ProviderCell from 'components/ProviderCell';
import PriceLabel from 'components/PriceLabel';

const { Option } = Select;

interface Props {
    isPractice?: boolean;
}

// Status label component with hover popover
const CredentialingStatusBadge: React.FC<{ status: ECredentialingDepositBillingStatus }> = ({ status }) => {
    const getStatusColor = (statusValue: ECredentialingDepositBillingStatus) => {
        switch (statusValue) {
            case ECredentialingDepositBillingStatus.SUCCEEDED:
                return s.statusSuccess;
            case ECredentialingDepositBillingStatus.PENDING:
            case ECredentialingDepositBillingStatus.PROCESSING:
                return s.statusProcessing;
            case ECredentialingDepositBillingStatus.FAILED:
                return s.statusError;
            case ECredentialingDepositBillingStatus.REFUND_PROCESSING:
                return s.statusProcessing;
            case ECredentialingDepositBillingStatus.REFUNDED:
                // Neutral blue: a refund is neither a success (money collected) nor an error.
                return s.statusRefunded;
            case ECredentialingDepositBillingStatus.PROVIDER_NOT_READY:
            case ECredentialingDepositBillingStatus.CANCELED:
            default:
                return s.statusDefault;
        }
    };

    const getStatusDescription = (statusValue: ECredentialingDepositBillingStatus) => {
        switch (statusValue) {
            case ECredentialingDepositBillingStatus.SUCCEEDED:
                return 'The refundable deposit for this panel has been charged to your payment method.';
            case ECredentialingDepositBillingStatus.PENDING:
                return 'The charge is in progress and will settle shortly.';
            case ECredentialingDepositBillingStatus.PROCESSING:
                return 'The charge is in progress and will settle shortly.';
            case ECredentialingDepositBillingStatus.FAILED:
                return "The charge didn't go through. Update your payment method to retry.";
            case ECredentialingDepositBillingStatus.PROVIDER_NOT_READY:
                return "This deposit hasn't been charged yet. It's collected once the panel is ready to submit to the payer.";
            case ECredentialingDepositBillingStatus.CANCELED:
                return 'This deposit charge has been canceled.';
            case ECredentialingDepositBillingStatus.REFUNDED:
                return 'This panel was closed and its deposit has been refunded to your payment method.';
            case ECredentialingDepositBillingStatus.REFUND_PROCESSING:
                return 'The refund for this panel has been issued and is on its way back to your payment method.';
            default:
                return '';
        }
    };

    const colorClass = getStatusColor(status);
    const description = getStatusDescription(status);

    return (
        <div className={`${s.statusTag} ${colorClass}`}>
            {ECredentialingDepositBillingStatusLabel[status]}
            <Popover
                content={<div className={s.pop}>{description}</div>}
                title={null}
                placement="right"
                trigger="hover"
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                        d="M5.48511 6C5.85118 5.22321 6.83895 4.66667 8.00004 4.66667C9.4728 4.66667 10.6667 5.5621 10.6667 6.66667C10.6667 7.59963 9.81496 8.38338 8.66285 8.6044C8.30125 8.67377 8.00004 8.96514 8.00004 9.33333M8 11.3333H8.00667M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8Z"
                        stroke="currentColor"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </Popover>
        </div>
    );
};

const CredentialingDeposits = ({ isPractice = false }: Props) => {
    const [selectedPeriod, setSelectedPeriod] = React.useState(1);
    const [searchDepositIdText, setSearchDepositIdText] = React.useState('');
    const [searchPendingDepositIdText, setSearchPendingDepositIdText] = React.useState('');
    const [searchPayerText, setSearchPayerText] = React.useState('');
    const [searchPendingPayerText, setSearchPendingPayerText] = React.useState('');
    const [searchProviderText, setSearchProviderText] = React.useState('');
    const [searchPendingProviderText, setSearchPendingProviderText] = React.useState('');
    const [page, setPage] = React.useState(1);
    const [orderType, setOrderType] = React.useState<'ASC' | 'DESC'>('DESC');
    const [orderKey, setOrderKey] = React.useState<string | undefined>(undefined);
    const [billingStatus, setBillingStatus] = React.useState<ECredentialingDepositBillingStatus[]>([]);
    const [viewingId, setViewingId] = React.useState<number | undefined>(undefined);

    const [dataList, listTotal, loading, refetch] = useCredentialingDepositList(undefined, isPractice);
    const [summary] = useCredentialingDepositSummary(selectedPeriod, isPractice);

    const hasActiveFilter = Boolean(
        searchDepositIdText || searchPayerText || searchProviderText || (billingStatus && billingStatus.length > 0),
    );
    // Only show the full-page empty state when there is genuinely no data and no
    // filter/search applied. A filter that returns zero rows should keep the table
    // (and its headers) visible so the user can clear it.
    const isEmptyData = !loading && !hasActiveFilter && (!dataList || dataList.length === 0);

    const handleTableChange = (
        paginationData: any,
        filtersData: Record<string, any> | null,
        sorter: any,
        action?: any,
    ) => {
        if (action?.action === 'paginate') {
            setPage(paginationData.current!);
            return;
        }

        if (action?.action === 'sort') {
            setPage(1);
            if (sorter?.order) {
                setOrderType(sorter.order === 'ascend' ? 'ASC' : 'DESC');
                if (sorter.column?.dataIndex === 'amountCents') {
                    setOrderKey('AMOUNT');
                } else if (sorter.column?.dataIndex === 'chargeSucceedAt') {
                    setOrderKey('CHARGE_SUCCEED_AT');
                } else {
                    setOrderKey(undefined);
                }
            } else {
                // sort cleared
                setOrderKey(undefined);
            }
            return;
        }

        if (action?.action === 'filter') {
            setPage(1);
            setBillingStatus(filtersData?.billingStatus || []);
        }
    };

    const refetchData = () => {
        const filter: any = {};
        filter.current = page;
        filter.size = 10;
        filter.periodMonths = selectedPeriod;
        if (orderKey) {
            filter.orderKey = orderKey;
        }
        if (orderType) {
            filter.orderType = orderType;
        }
        if (billingStatus && billingStatus.length > 0) {
            filter.billingStatus = billingStatus;
        }
        if (searchDepositIdText) {
            filter.depositId = parseInt(searchDepositIdText, 10);
        }
        if (searchPayerText) {
            filter.payerName = searchPayerText;
        }
        if (searchProviderText) {
            filter.providerName = searchProviderText;
        }
        refetch(filter);
    };

    const handleDownload = async (item: TCredentialingDepositItem) => {
        const baseURL = isLocalhost() ? 'https://dev.boxpractice.com/' : `${window.location.origin}/`;
        const url = `${baseURL}schedule-server/provider/stripe/invoice/${item.id}/pdf-url?billingId=${item.id}`;
        try {
            const response = await axios.get(url, {
                responseType: 'blob',
                headers: {
                    accept: 'application/json, text/plain, */*',
                    'accept-language': 'en-GB,en-US;q=0.9,en;q=0.8',
                    'cache-control': 'no-cache',
                    origin: `${baseURL}`,
                    pragma: 'no-cache',
                    referer: `${baseURL}`,
                    token: getUserToken(),
                },
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = downloadUrl;
            a.download = `deposit-${item.id}.pdf`;

            document.body.appendChild(a);
            a.click();

            window.URL.revokeObjectURL(downloadUrl);
            document.body.removeChild(a);
        } catch (err) {
            console.error('Download error:', err);
            message.error('Failed to download invoice');
        }
    };

    const handleView = async (item: TCredentialingDepositItem) => {
        setViewingId(item.id);
        try {
            const res = await getInvoiceViewUrl(item.id);
            if (!res.error) {
                const url = res.data.data;
                if (url) {
                    window.open(url, '_blank');
                } else {
                    message.error('Failed to load the invoice. Please try again.');
                }
            }
        } catch (err) {
            console.error('View invoice error:', err);
            message.error('Failed to load the invoice. Please try again.');
        } finally {
            setViewingId(undefined);
        }
    };

    React.useEffect(() => {
        refetchData();
    }, [
        page,
        orderType,
        orderKey,
        billingStatus,
        searchDepositIdText,
        searchPayerText,
        searchProviderText,
        selectedPeriod,
    ]);

    const getSortOrder = (key: string): 'ascend' | 'descend' | null => {
        if (orderKey !== key) {
            return null;
        }
        return orderType === 'ASC' ? 'ascend' : 'descend';
    };

    const allColumns: ColumnType<TCredentialingDepositItem>[] = [
        {
            title: 'DEPOSIT ID',
            dataIndex: 'id',
            key: 'id',
            width: 120,
            filterDropdown: (props: any) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="Search by deposit ID"
                        value={searchPendingDepositIdText}
                        onChange={(e) => setSearchPendingDepositIdText(e.target.value)}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => {
                                setPage(1);
                                setSearchDepositIdText(searchPendingDepositIdText);
                                props.confirm();
                            }}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                setPage(1);
                                setSearchPendingDepositIdText('');
                                setSearchDepositIdText('');
                                props.clearFilters();
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => (
                <SearchOutlined
                    style={{
                        color: filtered || searchDepositIdText ? '#1890ff' : undefined,
                    }}
                />
            ),
            onFilter: () => true,
        },
        ...(isPractice
            ? [
                  {
                      title: 'PROVIDER',
                      key: 'providerName',
                      width: 150,
                      filterDropdown: (props: any) => (
                          <div style={{ padding: 8 }}>
                              <Input
                                  placeholder="Search by provider"
                                  value={searchPendingProviderText}
                                  onChange={(e) => setSearchPendingProviderText(e.target.value)}
                                  onPressEnter={() => {
                                      setPage(1);
                                      setSearchProviderText(searchPendingProviderText);
                                      props.confirm();
                                  }}
                                  style={{ marginBottom: 8, display: 'block' }}
                              />
                              <Space>
                                  <Button
                                      type="primary"
                                      onClick={() => {
                                          setPage(1);
                                          setSearchProviderText(searchPendingProviderText);
                                          props.confirm();
                                      }}
                                      icon={<SearchOutlined />}
                                      size="small"
                                      style={{ width: 90 }}
                                  >
                                      Search
                                  </Button>
                                  <Button
                                      onClick={() => {
                                          setPage(1);
                                          setSearchPendingProviderText('');
                                          setSearchProviderText('');
                                          props.clearFilters();
                                      }}
                                      size="small"
                                      style={{ width: 90 }}
                                  >
                                      Reset
                                  </Button>
                              </Space>
                          </div>
                      ),
                      filterIcon: (filtered: boolean) => (
                          <SearchOutlined
                              style={{
                                  color: filtered || searchProviderText ? '#1890ff' : undefined,
                              }}
                          />
                      ),
                      onFilter: () => true,
                      render: (_: string, record: TCredentialingDepositItem) => (
                          <ProviderCell
                              name={record.providerName}
                              avatar={record.providerAvatar}
                              credential={record.providerCredential}
                          />
                      ),
                  } as ColumnType<TCredentialingDepositItem>,
              ]
            : []),
        {
            title: 'PAYER',
            dataIndex: 'payerName',
            key: 'payerName',
            width: 180,
            render: (payerName: string, record: TCredentialingDepositItem) =>
                record.payerState ? `${record.payerState} - ${payerName}` : payerName,
            filterDropdown: (props: any) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="Search by payer"
                        value={searchPendingPayerText}
                        onChange={(e) => setSearchPendingPayerText(e.target.value)}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => {
                                setPage(1);
                                setSearchPayerText(searchPendingPayerText);
                                props.confirm();
                            }}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                setPage(1);
                                setSearchPendingPayerText('');
                                setSearchPayerText('');
                                props.clearFilters();
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => (
                <SearchOutlined
                    style={{
                        color: filtered || searchPayerText ? '#1890ff' : undefined,
                    }}
                />
            ),
            onFilter: () => true,
        },
        {
            title: 'AMOUNT',
            dataIndex: 'amountCents',
            key: 'amountCents',
            width: 120,
            sorter: true,
            sortOrder: getSortOrder('AMOUNT'),
            render: (amountCents: number) => {
                return <div>${((amountCents || 0) / 100).toFixed(2)}</div>;
            },
        },
        {
            title: 'STATUS',
            dataIndex: 'billingStatus',
            key: 'billingStatus',
            width: 140,
            render: (status: ECredentialingDepositBillingStatus) => {
                return <CredentialingStatusBadge status={status} />;
            },
            filters: [
                {
                    value: ECredentialingDepositBillingStatus.PENDING,
                    text: ECredentialingDepositBillingStatusLabel.PENDING,
                },
                {
                    value: ECredentialingDepositBillingStatus.PROVIDER_NOT_READY,
                    text: ECredentialingDepositBillingStatusLabel.PROVIDER_NOT_READY,
                },
                {
                    value: ECredentialingDepositBillingStatus.PROCESSING,
                    text: ECredentialingDepositBillingStatusLabel.PROCESSING,
                },
                {
                    value: ECredentialingDepositBillingStatus.SUCCEEDED,
                    text: ECredentialingDepositBillingStatusLabel.SUCCEEDED,
                },
                {
                    value: ECredentialingDepositBillingStatus.FAILED,
                    text: ECredentialingDepositBillingStatusLabel.FAILED,
                },
                {
                    value: ECredentialingDepositBillingStatus.REFUND_PROCESSING,
                    text: ECredentialingDepositBillingStatusLabel.REFUND_PROCESSING,
                },
                {
                    value: ECredentialingDepositBillingStatus.REFUNDED,
                    text: ECredentialingDepositBillingStatusLabel.REFUNDED,
                },
            ],
            filteredValue: billingStatus,
            onFilter: () => true,
        },
        {
            title: 'CHARGED DATE',
            dataIndex: 'chargeSucceedAt',
            key: 'chargeSucceedAt',
            width: 160,
            sorter: true,
            sortOrder: getSortOrder('CHARGE_SUCCEED_AT'),
            render: (chargeSucceedAt: string | undefined | null) => {
                if (!chargeSucceedAt) {
                    return '-';
                }
                return <div>{dayjs(chargeSucceedAt).format('MM/DD/YYYY')}</div>;
            },
        },
        {
            title: '',
            key: 'action',
            fixed: 'right',
            width: 100,
            render: (record: TCredentialingDepositItem) => {
                const items: any = [
                    {
                        key: 'Download invoice',
                        label: (
                            <div
                                onClick={() => {
                                    handleDownload(record);
                                }}
                            >
                                Download invoice
                            </div>
                        ),
                    },
                ].filter(Boolean);
                return (
                    <div className={s.tableActions}>
                        <Button
                            type="link"
                            onClick={() => {
                                handleView(record);
                            }}
                            className={s.viewButton}
                            loading={viewingId === record.id}
                        >
                            View
                        </Button>
                        {items.length > 0 && (
                            <Dropdown
                                menu={{
                                    items,
                                }}
                                trigger={['hover']}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <path
                                        d="M12.0004 7.20039C10.6749 7.20039 9.60039 6.12587 9.60039 4.80039C9.60039 3.47491 10.6749 2.40039 12.0004 2.40039C13.3259 2.40039 14.4004 3.47491 14.4004 4.80039C14.4004 6.12587 13.3259 7.20039 12.0004 7.20039Z"
                                        fill="#6B7280"
                                    />
                                    <path
                                        d="M12.0004 14.4004C10.6749 14.4004 9.60039 13.3259 9.60039 12.0004C9.60039 10.6749 10.6749 9.60039 12.0004 9.60039C13.3259 9.60039 14.4004 10.6749 14.4004 12.0004C14.4004 13.3259 13.3259 14.4004 12.0004 14.4004Z"
                                        fill="#6B7280"
                                    />
                                    <path
                                        d="M9.60039 19.2004C9.60039 20.5259 10.6749 21.6004 12.0004 21.6004C13.3259 21.6004 14.4004 20.5259 14.4004 19.2004C14.4004 17.8749 13.3259 16.8004 12.0004 16.8004C10.6749 16.8004 9.60039 17.8749 9.60039 19.2004Z"
                                        fill="#6B7280"
                                    />
                                </svg>
                            </Dropdown>
                        )}
                    </div>
                );
            },
        },
    ];

    const columns = allColumns;

    return (
        <div className={s.wrap}>
            <div className={s.subNav}>
                <button type="button" className={s.activeNav}>
                    Panel deposits
                </button>
                <div className={s.periodSelect}>
                    <Select
                        value={selectedPeriod}
                        onChange={(value) => setSelectedPeriod(value)}
                        className={s.select}
                        style={{ minWidth: '160px' }}
                    >
                        <Option value={1}>Last 1 month</Option>
                        <Option value={2}>Last 2 months</Option>
                        <Option value={3}>Last 3 months</Option>
                        <Option value={4}>Last 4 months</Option>
                        <Option value={5}>Last 5 months</Option>
                        <Option value={6}>Last 6 months</Option>
                    </Select>
                </div>
            </div>

            {isEmptyData ? (
                <div className={s.emptyState}>
                    <h2>No credentialing charges yet</h2>
                    <p>
                        Your deposits, cashback, and billing activity will show up here as your panels are processed.
                        Nothing has been charged or collected yet.
                    </p>
                </div>
            ) : (
                <>
                    <div className={s.overviewSection}>
                        <div className={s.overviewSectionItem}>
                            <div className={s.h}>
                                <span>{isPractice ? 'Total deposits (practice)' : 'Total deposits'}</span>
                                <Popover
                                    trigger="hover"
                                    content={
                                        <div className={s.pop}>
                                            The total of the refundable deposits across all selected panels.
                                        </div>
                                    }
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                    >
                                        <path
                                            d="M13 16H12V12H11M12 8H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                                            stroke="#4B5563"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </Popover>
                            </div>
                            <div className={s.priceWrap}>
                                <PriceLabel value={summary ? summary.totalCents / 100 : undefined} />
                            </div>
                        </div>
                        <div className={s.overviewSectionItem}>
                            <div className={s.h}>
                                <span>Collected</span>
                                <Popover
                                    trigger="hover"
                                    content={
                                        <div className={s.pop}>Deposits already charged to the payment method.</div>
                                    }
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                    >
                                        <path
                                            d="M13 16H12V12H11M12 8H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                                            stroke="#4B5563"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </Popover>
                            </div>
                            <div className={s.priceWrap}>
                                <PriceLabel value={summary ? summary.collectedCents / 100 : undefined} />
                            </div>
                        </div>
                        <div className={s.overviewSectionItem}>
                            <div className={s.h}>
                                <span>Outstanding</span>
                                <Popover
                                    trigger="hover"
                                    content={
                                        <div className={s.pop}>
                                            Deposits not yet collected (pending, failed, or not started).
                                        </div>
                                    }
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                    >
                                        <path
                                            d="M13 16H12V12H11M12 8H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                                            stroke="#4B5563"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </Popover>
                            </div>
                            <div className={s.priceWrap}>
                                <PriceLabel value={summary ? summary.outstandingCents / 100 : undefined} />
                            </div>
                        </div>
                    </div>

                    <div className={s.tableWrap}>
                        <CommonTable
                            className={`commonTable ${s.depositsTable}`}
                            columns={columns}
                            data={dataList || []}
                            loading={loading}
                            bordered
                            rowKey={(record) => `${record.id}-${record.payerState ?? ''}-${record.payerName ?? ''}`}
                            onChange={handleTableChange}
                            pagination={{
                                pageSize: 10,
                                current: page,
                                showSizeChanger: false,
                                total: listTotal,
                                showTotal: (total) => `${total} records`,
                            }}
                            scroll={{ x: 'max-content' }}
                            locale={{
                                emptyText: 'No deposits yet',
                            }}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default CredentialingDeposits;
